import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import PermissionGate from "@/components/PermissionGate";
import PlacementDialog from "@/components/placement/PlacementDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Briefcase, Award, TrendingUp, Users, IndianRupee, Trophy } from "lucide-react";

const ELIG_STYLE = { Eligible: "bg-accent/10 text-accent", Pending: "bg-amber-100 text-amber-700", "Not Eligible": "bg-slate-100 text-slate-600" };
const RESUME_STYLE = { "Not Started": "bg-slate-100 text-slate-600", "In Review": "bg-amber-100 text-amber-700", Ready: "bg-accent/10 text-accent" };

const KPI = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className={`h-4 w-4 ${accent || "text-primary"}`} /><span className="text-xs">{label}</span></div>
    <p className="mt-1 font-display text-2xl font-bold text-secondary">{value}</p>
  </div>
);

export default function Placement() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [meta, setMeta] = useState({ eligibility: [], resume_statuses: [], refund_eligibility: [] });
  const [search, setSearch] = useState("");
  const [eligFilter, setEligFilter] = useState("all");
  const [placedFilter, setPlacedFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/placement", {
      params: { search: search || undefined, eligibility: eligFilter, placed: placedFilter },
    });
    setRows(data.items || []);
    const { data: d } = await api.get("/placement/dashboard");
    setStats(d);
  }, [search, eligFilter, placedFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/placement/meta").then(({ data }) => setMeta(data)); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete placement record ${r.jg_code}?`)) return;
    try { await api.delete(`/placement/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="placement-page">
      <PageHeader title="Placement" subtitle="Job-guarantee tracking from eligibility to offer." />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KPI icon={Users} label="Eligible" value={stats.eligible ?? 0} />
        <KPI icon={TrendingUp} label="In Process" value={stats.in_process ?? 0} accent="text-blue-600" />
        <KPI icon={Award} label="Placed" value={stats.placed ?? 0} accent="text-accent" />
        <KPI icon={Briefcase} label="Placement %" value={`${stats.placement_rate ?? 0}%`} />
        <KPI icon={IndianRupee} label="Avg Package" value={`${stats.avg_package ?? 0} LPA`} />
        <KPI icon={Trophy} label="Highest" value={`${stats.highest_package ?? 0} LPA`} accent="text-amber-600" />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search student, company, code..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-72 pl-9" data-testid="placement-search" />
          </div>
          <Select value={eligFilter} onValueChange={setEligFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="placement-eligibility-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All eligibility</SelectItem>{meta.eligibility.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={placedFilter} onValueChange={setPlacedFilter}>
            <SelectTrigger className="h-9 w-36" data-testid="placement-placed-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="true">Placed</SelectItem><SelectItem value="false">Not placed</SelectItem></SelectContent>
          </Select>
        </div>
        <PermissionGate permission="jobguarantee.create">
          <Button onClick={() => { setEditRow(null); setDialogOpen(true); }} data-testid="add-placement-button"><Plus className="mr-1.5 h-4 w-4" /> New record</Button>
        </PermissionGate>
      </div>

      <div className="rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Student</TableHead><TableHead>Course</TableHead>
              <TableHead>Eligibility</TableHead><TableHead>Resume</TableHead><TableHead>Progress</TableHead>
              <TableHead>Outcome</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No placement records yet.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`placement-row-${r.jg_code}`}>
                <TableCell className="font-medium">{r.jg_code}</TableCell>
                <TableCell className="font-medium text-secondary">{r.student_name}<br /><span className="text-xs text-muted-foreground">{r.student_code}</span></TableCell>
                <TableCell className="text-sm">{r.course_name}</TableCell>
                <TableCell><Badge className={`border-0 ${ELIG_STYLE[r.eligibility] || ""}`}>{r.eligibility}</Badge></TableCell>
                <TableCell><Badge className={`border-0 ${RESUME_STYLE[r.resume_status] || ""}`}>{r.resume_status}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.applications_sent} apps · {r.interviews_scheduled} intv · {r.offers_received} offers</TableCell>
                <TableCell>
                  {r.placed
                    ? <span className="text-sm"><Badge className="border-0 bg-accent/10 text-accent">Placed</Badge><br /><span className="text-xs text-muted-foreground">{r.company} · {r.package_lpa} LPA</span></span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <PermissionGate permission="jobguarantee.update"><Button variant="ghost" size="sm" onClick={() => { setEditRow(r); setDialogOpen(true); }} data-testid={`edit-placement-${r.jg_code}`}><Pencil className="h-4 w-4" /></Button></PermissionGate>
                  <PermissionGate permission="jobguarantee.delete"><Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(r)} data-testid={`delete-placement-${r.jg_code}`}><Trash2 className="h-4 w-4" /></Button></PermissionGate>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PlacementDialog open={dialogOpen} onOpenChange={setDialogOpen} editRow={editRow} meta={meta} onSaved={load} />
    </div>
  );
}
